import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { Grid } from '@mantine/core'
import useStyles from './HowMuchIsAMusicLicense.styles'
import { isKorean, translate } from '../../../../utilities/language'
import { getAttribution } from '../../../../utilities/content'
import {
  ARTICLE_IMAGE_AVIF,
  ARTICLE_IMAGE_JPEG,
  HOW_MUCH_TITLE_HTML,
  HOW_MUCH_TITLE_HTML_ES,
  HOW_MUCH_TITLE_HTML_FR,
  HOW_MUCH_TITLE_HTML_KO,
  HOW_MUCH_ARTICLE_PUBLISHED_STR,
} from '../../../../static-data/how-much-is-a-music-license-for-a-small-shop-data'
import { IconExternalLink } from '@tabler/icons'
import {
  MAIN_SITE_ENVIRONMENT_HOST,
  MAIN_SITE_ENVIRONMENT_HOST_ES,
  MAIN_SITE_ENVIRONMENT_HOST_FR,
  MAIN_SITE_ENVIRONMENT_HOST_KO,
} from '../../../../utilities/config'

const BackToArticle: React.ComponentType<any> = dynamic(
  () => import('../../../../components/controls/BackToArticle'),
  {
    ssr: true,
  }
)
const ReadyToTransform: React.ComponentType<any> = dynamic(
  () => import('../../../../components/controls/ReadyToTransform'),
  {
    ssr: true,
  }
)

export default function HowMuchIsAMusicLicense(props: any) {
  const { classes } = useStyles()
  const selectedLanguage: any = props?.selectedLanguage
  const width: any = props?.width
  const avifCompatible = props?.avifCompatible

  const articleImage = useMemo(() => {
    return (
      <Image
        width={512}
        height={279}
        sizes="(max-width: 350px) 256px, 
               (max-width: 430px) 300px, 
               (max-width: 590px) 384px, 
               512px"
        src={avifCompatible ? ARTICLE_IMAGE_AVIF : ARTICLE_IMAGE_JPEG}
        alt=""
        priority={true}
        style={{
          marginTop: '30px',
          borderRadius: '10px',
          display: 'block',
          width: '100%',
          maxWidth: '512px',
          height: 'auto',
        }}
      />
    )
  }, [avifCompatible])

  return (
    <div className={classes.mainGrid}>
      <div className={classes.innerDiv}>
        <Grid columns={24}>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <h1 className={classes.standardText + ' ' + classes.h1Tag}>
                {translate(
                  HOW_MUCH_TITLE_HTML,
                  HOW_MUCH_TITLE_HTML_ES,
                  HOW_MUCH_TITLE_HTML_FR,
                  HOW_MUCH_TITLE_HTML_KO,
                  selectedLanguage
                )}
              </h1>
              {getAttribution(
                translate(
                  'Editor',
                  'Editora',
                  'Éditrice',
                  '편집자',
                  selectedLanguage
                ),
                HOW_MUCH_ARTICLE_PUBLISHED_STR,
                selectedLanguage,
                false
              )}
              {articleImage}
            </div>
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Whenever you have entered a boutique or a coffee shop playing
                  background music, you have experienced how music transforms a
                  retail environment. From creating the right mood to enhancing
                  customer dwell time, music is a great business tool. But did
                  you know that playing music in a commercial setting needs to
                  be licensed?
                </>,
                <>
                  Siempre que ha entrado en una boutique o cafetería con música
                  de fondo, ha experimentado cómo la música transforma un
                  entorno minorista. Desde crear el ambiente adecuado hasta
                  mejorar el tiempo de permanencia del cliente, la música es una
                  gran herramienta de negocio. ¿Pero sabías que para reproducir
                  música en un entorno comercial es necesario contar con una
                  licencia?
                </>,
                <>
                  Chaque fois que vous entrez dans une boutique ou un café où de
                  la musique est diffusée en fond sonore, vous constatez à quel
                  point la musique transforme l'atmosphère d'un établissement
                  commercial. Qu'il s'agisse de créer la bonne ambiance ou
                  d'inciter les clients à rester plus longtemps, la musique est
                  un excellent outil commercial. Mais saviez-vous que la
                  diffusion de musique dans un établissement commercial
                  nécessite une licence&nbsp;?
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    부티크나
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    카페에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    들어가서
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    배경
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    흘러나오는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    경험해
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    본
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    적이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    있다면,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소매
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    환경을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    어떻게
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    변화시키는지
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    직접
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    느껴보셨을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    겁니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    적절한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    분위기를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    조성하는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것부터
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    고객
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    체류
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    시간을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    늘리는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것까지,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    훌륭한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    비즈니스
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    도구입니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    하지만
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    상업
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    공간에서
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    재생하려면
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    라이선스가
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    필요하다는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사실을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    알고
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    계셨나요?
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  However, it is not as simple as playing your favorite playlist
                  when it comes to playing music in your retail store. Even
                  playing music via speakers, a radio, or even a streaming app
                  in your shop falls under the category of public performance,
                  which implies that you have to get the rights holder’s
                  permission. Without the right music license, commercial use of
                  music may attract a penalty or legal prosecution.
                </>,
                <>
                  Sin embargo, no es tan sencillo como reproducir tu lista de
                  reproducción favorita cuando se trata de reproducir música en
                  tu tienda minorista. Incluso reproducir música a través de
                  altavoces, una radio o incluso una aplicación de streaming en
                  tu tienda entra en la categoría de interpretación pública, lo
                  que implica que debes obtener el permiso del titular de los
                  derechos. Sin la licencia musical adecuada, el uso comercial
                  de la música puede conllevar una sanción o un proceso legal.
                </>,
                <>
                  Cependant, diffuser de la musique dans un magasin n'est pas
                  aussi simple que d'écouter sa playlist préférée. Même la
                  diffusion de musique via des haut-parleurs, une radio ou une
                  application de streaming dans votre boutique est considérée
                  comme une exécution publique, ce qui implique d'obtenir
                  l'autorisation des ayants droit. Sans la licence musicale
                  appropriée, l'utilisation commerciale de musique peut
                  entraîner des sanctions ou des poursuites judiciaires.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    하지만
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소매점에서
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    트는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    좋아하는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    플레이리스트를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    재생하는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것만큼
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    간단하지
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    않습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    매장에서
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    스피커,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    라디오
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    또는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    스트리밍
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    앱을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    통해
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    재생하는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것조차도
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    공공
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    공연에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    해당하므로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    저작권자의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    허가를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    받아야
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    합니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    적절한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    라이선스
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    없이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    상업적으로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사용하면
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    벌금이나
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    법적
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소송을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    당할
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    수
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    있습니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  This blog will take you through all you need to know about the
                  legal use of music in business, from music license costs for
                  retail shops to knowledge of various licensing organizations
                  in the UK and the USA. Whether you run a chic boutique or a
                  cozy coffee shop, this guide will help you to be not only
                  legal but also musically sorted.
                </>,
                <>
                  Este blog le explicará todo lo que necesita saber sobre el uso
                  legal de la música en los negocios, desde los costos de las
                  licencias de música para tiendas minoristas hasta el
                  conocimiento de varias organizaciones de licencias en el Reino
                  Unido y los EE. UU. Ya sea que tenga una boutique elegante o
                  una cafetería acogedora, esta guía le ayudará a estar no sólo
                  legalmente sino también musicalmente.
                </>,
                <>
                  Ce blog vous expliquera tout ce que vous devez savoir sur
                  l'utilisation légale de la musique en entreprise, des coûts
                  des licences musicales pour les commerces de détail aux
                  différentes sociétés de gestion des droits d'auteur au
                  Royaume-Uni et aux États-Unis. Que vous dirigiez une boutique
                  branchée ou un café chaleureux, ce guide vous aidera à
                  respecter la loi et à choisir la musique idéale pour votre
                  établissement.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    블로그에서는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소매점의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사용료부터
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    영국과
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    미국의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    다양한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    저작권
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    관리
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    단체에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    대한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    정보까지,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사업장에서
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    합법적으로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사용하는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    데
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    필요한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    모든
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    알려드립니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    세련된
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    부티크를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    운영하든
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    아늑한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    카페를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    운영하든,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    가이드를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    통해
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    합법적인
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사용은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    물론,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    매장에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    어울리는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    선택하는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    데에도
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    도움을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    받으실
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    수
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    있을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것입니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <hr className={classes.threeDotDivisor} />
            <section id="small-need-a-music-license">
              <h2 className={classes.subSection}>
                {translate(
                  <>Do Small Shops Need a Music License?</>,
                  <>¿Las pequeñas tiendas necesitan una licencia de música?</>,
                  <>
                    Les petits commerces ont-ils besoin d'une licence de
                    diffusion musicale ?
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소규모
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상점도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      허가가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요할까요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>Brief answer: Yes, practically in each country.</>,
                  <>Respuesta breve: Sí, prácticamente en cada país.</>,
                  <>Réponse courte&nbsp;: Oui, pratiquement tous les pays.</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      간단히
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      말씀드리자면,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      네,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      거의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      나라에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가능합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    A music license is required by you if your business
                    entertains with music in any of the public places, whether
                    through speakers, live bands, disc jockeys, or even TVs
                    showing music videos. This is applicable throughout
                    industries, whether it is hair salons, yoga studios, cafes,
                    or boutiques.
                  </>,
                  <>
                    Usted requiere una licencia de música si su empresa
                    entretiene con música en cualquiera de los lugares públicos,
                    ya sea a través de parlantes, bandas en vivo, disc jockeys o
                    incluso televisores que muestran videos musicales. Esto se
                    aplica a todas las industrias, ya sean peluquerías, estudios
                    de yoga, cafeterías o boutiques.
                  </>,
                  <>
                    Une licence musicale est obligatoire si votre entreprise
                    diffuse de la musique dans des lieux publics, que ce soit
                    par le biais de haut-parleurs, de groupes de musique, de DJ
                    ou même de téléviseurs diffusant des clips vidéo. Cette
                    obligation s'applique à tous les secteurs d'activité, qu'il
                    s'agisse de salons de coiffure, de studios de yoga, de cafés
                    ou de boutiques.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사업장에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스피커,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이브
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      밴드,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      디스크
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      자키
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      또는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뮤직
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비디오를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      TV
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      등을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      통해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      틀어
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      고객에게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      즐거움을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제공하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      경우,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저작권
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      허가가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      미용실,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스튜디오,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부티크
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      등
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업종에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      해당됩니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Why? Because music creators and performers own public
                    performance rights. It violates their rights to play their
                    work in a business without their permission, even though you
                    may have legally bought the music for personal use.
                  </>,
                  <>
                    ¿Por qué? Porque los creadores e intérpretes musicales
                    poseen los derechos de interpretación pública. Viola sus
                    derechos reproducir su trabajo en una empresa sin su
                    permiso, aunque haya comprado legalmente la música para uso
                    personal.
                  </>,
                  <>
                    Pourquoi&nbsp;? Parce que les créateurs et interprètes de
                    musique détiennent les droits d'exécution publique. Diffuser
                    leur œuvre dans un commerce sans leur autorisation constitue
                    une violation de leurs droits, même si vous avez légalement
                    acheté la musique pour votre usage personnel.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      왜냐하면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      창작자와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연주자는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공개
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공연에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      권리를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소유하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      때문입니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개인적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      용도로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합법적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구매했더라도,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그들의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      허락
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사업장에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그들의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      권리를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      침해하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      행위입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="examples-of-affected-businesses">
              <h2 className={classes.subSection}>
                {translate(
                  <>Examples of Affected Businesses:</>,
                  <>Ejemplos de empresas afectadas:</>,
                  <>Exemples d'entreprises concernées&nbsp;:</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      영향을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      받는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사업체의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      예:
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>Coffee shops playing jazz playlists</>,
                    <>Cafeterías con listas de reproducción de jazz</>,
                    <>Cafés diffusant des listes de lecture de jazz</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재즈
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        플레이리스트를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        틀어주는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        커피숍
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Spas using instrumental background music</>,
                    <>Spas con música de fondo instrumental</>,
                    <>Spas utilisant de la musique d'ambiance instrumentale</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        배경
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사용하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스파
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Retail stores that play popular hits</>,
                    <>Tiendas minoristas que reproducen éxitos populares</>,
                    <>Magasins de détail qui diffusent des tubes populaires</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        히트곡을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        틀어주는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소매점
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Even small-scale businesses may find themselves on the wrong
                    side of the law in the absence of the appropriate music
                    licensing requirements for small retail businesses. A
                    license is always required unless proven otherwise.
                  </>,
                  <>
                    Incluso las pequeñas empresas pueden encontrarse en el lado
                    equivocado de la ley si no existen requisitos de licencia de
                    música adecuados para las pequeñas empresas minoristas.
                    Siempre se requiere una licencia a menos que se demuestre lo
                    contrario.
                  </>,
                  <>
                    Même les petites entreprises peuvent se retrouver en
                    infraction avec la loi si elles ne respectent pas les
                    exigences en matière de licences musicales pour les petits
                    commerces de détail. Une licence est toujours requise, sauf
                    preuve du contraire.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소규모
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소매업체라도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적절한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저작권
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      허가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요건을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      충족하지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      못하면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      법적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      문제에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      휘말릴
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특별한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      예외
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사항이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      입증되지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      한,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용에는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      항상
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      허가가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <hr className={classes.threeDotDivisor} />
              <section id="cost-of-music-license-for-small-shop">
                <h2 className={classes.subSection}>
                  {translate(
                    <>How Much Is a Music License for a Small Shop?</>,
                    <>
                      ¿Cuánto cuesta una licencia de música para una pequeña
                      tienda?
                    </>,
                    <>
                      Quel est le prix d'une licence musicale pour un petit
                      commerce&nbsp;?
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소규모
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        매장에서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사용하려면
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비용이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        얼마나
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        드나요?
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h2>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      The cost to legally play music in a business depends on
                      factors like your location, store size, type of music
                      usage, and whether the music is recorded or live. The
                      snapshot below shows the music license cost for retail
                      shops in popular areas:
                    </>,
                    <>
                      El costo de reproducir música legalmente en una empresa
                      depende de factores como la ubicación, el tamaño de la
                      tienda, el tipo de uso de la música y si la música está
                      grabada o en vivo. La siguiente instantánea muestra el
                      costo de la licencia de música para tiendas minoristas en
                      áreas populares:
                    </>,
                    <>
                      Le coût de la diffusion légale de musique dans un commerce
                      dépend de plusieurs facteurs, tels que la localisation, la
                      taille du magasin, le type d'utilisation de la musique et
                      le fait qu'il s'agisse de musique enregistrée ou jouée en
                      direct. Le tableau ci-dessous présente le coût des
                      licences musicales pour les commerces de détail situés
                      dans des zones fréquentées&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사업장에서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        합법적으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재생하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        드는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비용은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위치,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        매장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        규모,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사용
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방식,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        그리고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        녹음된
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악인지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이브
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악인지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        등
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        여러
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요인에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        따라
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        달라집니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        아래
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        표는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지역에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위치한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소매점에서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이선스를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        취득하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        드는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비용을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        간략하게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보여줍니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <div className={classes.dataTableWrapper}>
                  <table className={classes.dataTable}>
                    <caption className={classes.dtCaption}>
                      {translate(
                        <>
                          Country by Music Performing Rights Organization (PRO)
                          by Typical Annual Cost for Small Retailers
                        </>,
                        <>
                          Países según la organización de gestión de derechos
                          musicales y el coste anual típico para pequeños
                          minoristas.
                        </>,
                        <>
                          Pays par organisme de gestion des droits musicaux et
                          coût annuel typique pour les petits détaillants
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            음악 저작권 관리 단체에 따르면 각 국가별 소규모
                            소매업체의 일반적인 연간 비용은 다음과 같습니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>Country</>,
                            <>País</>,
                            <>Pays</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                국가
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>License&nbsp;Provider</>,
                            <>Proveedor de&nbsp;licencia</>,
                            <>Fournisseur de&nbsp;licences</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                라이선스
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                제공자
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>
                              Typical&nbsp;Annual&nbsp;Cost (Small&nbsp;Shops)
                            </>,
                            <>
                              Costo&nbsp;anual&nbsp;típico
                              (tiendas&nbsp;pequeñas)
                            </>,
                            <>
                              Coût&nbsp;annuel&nbsp;typique
                              (petits&nbsp;magasins)
                            </>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                일반적인
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                연간
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                비용
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                (소규모
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                상점)
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className={classes.dataTableField}>UK</td>
                        <td className={classes.dataTableField}>
                          PRS&nbsp;+&nbsp;PPL
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>£150&nbsp;–&nbsp;£300&nbsp;combined</>,
                            <>£150&nbsp;–&nbsp;£300&nbsp;combinados</>,
                            <>Entre 150 et 300 £ au total</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                총
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                150파운드에서
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                300파운드
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                사이
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>USA</>,
                            <>EE.UU</>,
                            <>USA</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                미국
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          ASCAP&nbsp;/&nbsp;BMI
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>$250&nbsp;–&nbsp;$500&nbsp;per&nbsp;PRO</>,
                            <>$250&nbsp;-&nbsp;$500&nbsp;por&nbsp;PRO</>,
                            <>250&nbsp;$&nbsp;à&nbsp;500&nbsp;$ par&nbsp;PRO</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                PRO당
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                250달러
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                ~
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                500달러
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>
                          {isKorean(selectedLanguage) ? <>호주</> : <>AUS</>}
                        </td>
                        <td className={classes.dataTableField}>
                          OneMusic Australia
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>AUD&nbsp;200&nbsp;–&nbsp;$400</>,
                            <>AUD&nbsp;200&nbsp;–&nbsp;$400</>,
                            <>200 AUD – 400 $</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                호주
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                달러
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                200달러
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                –
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                400달러
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>
                          {isKorean(selectedLanguage) ? (
                            <>캐나다</>
                          ) : (
                            <>Canada</>
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          SOCAN, Re:Sound
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>CAD&nbsp;200&nbsp;–&nbsp;$400</>,
                            <>CAD&nbsp;200&nbsp;–&nbsp;$400</>,
                            <>200&nbsp;à&nbsp;400 dollars&nbsp;canadiens</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                캐나다
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                달러
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                200~400달러
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
              <section id="key-factors-affecting-cost">
                <h3 className={classes.subSection}>
                  {translate(
                    <>Key Factors Affecting Cost:</>,
                    <>Factores clave que afectan el costo:</>,
                    <>Facteurs clés influençant le coût&nbsp;:</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비용에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영향을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        미치는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주요
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요인:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Square footage:
                        </span>{' '}
                        The bigger stores will pay more.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Metros cuadrados:
                        </span>{' '}
                        las tiendas más grandes pagarán más.
                      </>,
                      <>Les&nbsp;grands magasins&nbsp;paieront davantage.</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          매장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          면적:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          매장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          규모가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          클수록
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          임대료가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          더
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          비쌉니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Music:</span> Live
                        bands, DJs, or canned music sets will incur an extra
                        charge.
                      </>,
                      <>
                        <span className={classes.spanBolder}>Música:</span>{' '}
                        bandas en vivo, DJ o sets de música enlatados tendrán un
                        cargo adicional.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Musique&nbsp;:
                        </span>{' '}
                        Les groupes de musique en direct, les DJ ou les sets de
                        musique préenregistrée entraîneront des frais
                        supplémentaires.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          라이브
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          밴드,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          DJ
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          또는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          녹음된
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          재생에는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          추가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          요금이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          부과됩니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Number of Locations:
                        </span>{' '}
                        The more the branches, the more the licenses.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Número de Ubicaciones:
                        </span>{' '}
                        Cuantas más sucursales, más licencias.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Nombre d'emplacements&nbsp;:
                        </span>{' '}
                        Plus il y a de succursales, plus il y a de licences.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          지점
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          지점
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          많을수록
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          필요한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          라이선스
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수도
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          많아집니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Opening hours:
                        </span>{' '}
                        Long opening hours can raise the charges.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Horario de apertura:
                        </span>{' '}
                        Los horarios de apertura prolongados pueden aumentar los
                        cargos.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Horaires d'ouverture&nbsp;:
                        </span>{' '}
                        Les horaires d'ouverture prolongés peuvent entraîner des
                        frais supplémentaires.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          영업
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          시간:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          영업
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          시간이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          길어지면
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          요금이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          인상될
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          있습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Customer capacity:
                        </span>{' '}
                        Larger capacity establishments will pay higher.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Capacidad de clientes:
                        </span>{' '}
                        Los establecimientos de mayor capacidad pagarán más.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Capacité d'accueil des clients&nbsp;:
                        </span>{' '}
                        Les établissements ayant une plus grande capacité
                        paieront davantage.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고객
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수용
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          능력:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수용
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          인원이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          많은
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          업소일수록
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          더
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          높은
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          요금을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          지불해야
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Music usage:</span>{' '}
                        Background vs. featured entertainment
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Uso de música:
                        </span>{' '}
                        entretenimiento de fondo versus entretenimiento
                        destacado
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Utilisation de la musique&nbsp;:
                        </span>{' '}
                        Musique d'ambiance ou animation principale
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          사용
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          방식:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          배경
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          vs.
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          주요
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          엔터테인먼트
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          요소
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      For example, a 500 sq. ft. shop in the UK can expect to
                      pay approximately £250/year in combined PRS music license
                      cost and PPL license pricing to play background radio or
                      playlists.
                    </>,
                    <>
                      Por ejemplo, una tienda de 500 pies cuadrados en el Reino
                      Unido puede esperar pagar aproximadamente £250 al año en
                      costo combinado de licencia de música PRS y precio de
                      licencia PPL para reproducir radio de fondo o listas de
                      reproducción.
                    </>,
                    <>
                      Par exemple, un magasin de 500 pieds carrés au Royaume-Uni
                      peut s'attendre à payer environ 250 £ par an en frais de
                      licence musicale (licences PRS et PPL combinées) pour
                      diffuser de la musique d'ambiance à la radio ou via des
                      listes de lecture.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        예를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        들어,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영국에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        500제곱피트
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        규모의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        매장은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        배경
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라디오나
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        플레이리스트를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재생하기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        PRS
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비용과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        PPL
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비용을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        합쳐
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연간
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        약
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        250파운드를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지불해야
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        예상됩니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Pro tip: When asking questions like how much to play music
                    in a store, it is always good to consult the relevant
                    licensing organization to get the latest rates.
                  </>,
                  <>
                    Consejo profesional: al hacer preguntas como cuánto cuesta
                    reproducir música en una tienda, siempre es bueno consultar
                    a la organización de licencias correspondiente para obtener
                    las tarifas más recientes.
                  </>,
                  <>
                    Conseil de pro&nbsp;: Lorsque vous posez des questions
                    telles que le coût de la diffusion de musique dans un
                    magasin, il est toujours conseillé de consulter l'organisme
                    de gestion des droits d'auteur compétent afin d'obtenir les
                    tarifs les plus récents.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전문가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      팁:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매장에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      트는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      데
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      드는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비용과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      같은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      질문을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      때는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관련
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저작권
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      단체에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      문의하여
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      최신
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요금을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      확인하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      좋습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisor} />
            <section id="top-music-licensing-companies-and-services">
              <h2 className={classes.subSection}>
                {translate(
                  <>Top Music Licensing Companies and Services</>,
                  <>Principales empresas y servicios de licencias de música</>,
                  <>Principales sociétés et services de licence musicale</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      최고의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      회사
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      및
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서비스
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id="background-music-licensor-coverage">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>So, let us examine the most typical providers:</>,
                    <>Entonces, examinemos los proveedores más típicos:</>,
                    <>
                      Examinons donc les fournisseurs les plus typiques&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        그럼
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        대표적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        제공업체들을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        살펴보겠습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <div className={classes.dataTableWrapper}>
                  <table className={classes.dataTable}>
                    <caption className={classes.dtCaption}>
                      {translate(
                        <>
                          Music Licensing Body or Streaming Service/Licensor
                          Bundler by Country and License Scope
                        </>,
                        <>
                          Organizaciones de gestión de derechos musicales o
                          proveedores de servicios de streaming/licencias
                          combinados por país y alcance de la licencia.
                        </>,
                        <>
                          Organismes de gestion des droits musicaux ou offres
                          groupées de services de streaming/fournisseurs de
                          licences par pays et par étendue de la licence.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            국가 및 라이선스 범위별 음악 라이선스 기관 또는
                            스트리밍 서비스/라이선스 제공업체 번들러
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>Licensing&nbsp;Body</>,
                            <>Organismo&nbsp;de concesión de&nbsp;licencias</>,
                            <>Concédant de&nbsp;licence</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                라이선스
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                제공자
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>Country</>,
                            <>País</>,
                            <>Pays</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                국가
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>Coverage</>,
                            <>Cobertura</>,
                            <>Couverture</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                적용
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                범위
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className={classes.dataTableField}>
                          PRS&nbsp;for&nbsp;Music
                        </td>
                        <td className={classes.dataTableField}>UK</td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Composers and&nbsp;publishers</>,
                            <>Compositores y&nbsp;editores</>,
                            <>Compositeurs et&nbsp;éditeurs</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                작곡가
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                및
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                출판사
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>PPL</td>
                        <td className={classes.dataTableField}>UK</td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Performers and labels</>,
                            <>Artistas y&nbsp;sellos</>,
                            <>Artistes et&nbsp;labels</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                공연자와
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                레이블
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>ASCAP</td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>USA</>,
                            <>EE.UU</>,
                            <>USA</>,
                            <>미국</>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Composers and publishers</>,
                            <>Compositores y&nbsp;editores</>,
                            <>Compositeurs et&nbsp;éditeurs</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                작곡가
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                및
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                출판사
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>BMI</td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>USA</>,
                            <>EE.UU</>,
                            <>USA</>,
                            <>미국</>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Songwriters, composers, and&nbsp;publishers</>,
                            <>Compositores, compositores y&nbsp;editores</>,
                            <>
                              Auteurs-compositeurs, compositeurs&nbsp;et
                              éditeurs&nbsp;de&nbsp;musique
                            </>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                작곡가,
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                작사가
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                및
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                출판사
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>SESAC</td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>USA</>,
                            <>EE.UU</>,
                            <>USA</>,
                            <>미국</>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Invitation-only, exclusive artists</>,
                            <>
                              Artistas exclusivos&nbsp;solo por&nbsp;invitación
                            </>,
                            <>
                              Artistes sélectionnés&nbsp;sur
                              invitation&nbsp;uniquement
                            </>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                초대받은
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                사람만
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                참여할
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                수
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                있는,
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                특별한
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                아티스트들
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>
                          SOCAN / Re:Sound
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Canada</>,
                            <>Canadá</>,
                            <>Canada</>,
                            <>캐나다</>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Similar to PRS/PPL</>,
                            <>Similar a PRS/PPL</>,
                            <>Similaire à PRS/PPL</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                PRS/PPL와
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                유사합니다.
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>
                          Cloud Cover Music
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>USA</>,
                            <>EE.UU</>,
                            <>USA</>,
                            <>미국</>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>All-in-one licensing + music delivery</>,
                            <>
                              Licencias todo&nbsp;en&nbsp;uno +&nbsp;entrega
                              de&nbsp;música
                            </>,
                            <>
                              Licences&nbsp;et distribution musicale tout-en-un
                            </>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                올인원
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                라이선스
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                및
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                음악
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                배포
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                서비스
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Contemporary substitutions comprise commercial providers of
                    music rights, such as
                  </>,
                  <>
                    Las sustituciones contemporáneas comprenden proveedores
                    comerciales de derechos musicales, como
                  </>,
                  <>
                    Les alternatives contemporaines comprennent des fournisseurs
                    commerciaux de droits musicaux, tels que
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      현대적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대안으로는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      같은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저작권
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제공업체들이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Soundstripe:</span>{' '}
                      $20-$83/month for commercial use
                    </>,
                    <>
                      <span className={classes.spanBolder}>Soundstripe:</span>{' '}
                      $20-$83/mes para uso comercial
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Soundstripe&nbsp;:
                      </span>{' '}
                      20 à 83 dollars par mois pour un usage commercial
                    </>,
                    <>
                      <span className={classes.spanBolder}>Soundstripe:</span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상업적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        용도로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사용할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        경우
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        월
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        20달러에서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        83달러입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Epidemic Sound:
                      </span>{' '}
                      $17-$30/month for business licensing
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Epidemic Sound:
                      </span>{' '}
                      $17-$30/mes para licencias comerciales
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Epidemic Sound&nbsp;:
                      </span>{' '}
                      17 à 30 dollars par mois pour l'obtention d'une licence
                      commerciale
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Epidemic Sound:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사업자
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비용은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        월
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        17달러에서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        30달러입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Audio Jungle:</span>{' '}
                      Pay-per-track commercial licensing
                    </>,
                    <>
                      <span className={classes.spanBolder}>Audio Jungle:</span>{' '}
                      Licencias comerciales de pago por pista
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Audio Jungle&nbsp;:
                      </span>{' '}
                      Licences commerciales par titre musical
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Audio Jungle&nbsp;:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        트랙별
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유료
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상업용
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이선스
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    These services offer pre-cleared music libraries,
                    eliminating the need for multiple organization licenses
                    while offering cost-effective solutions to play music in a
                    business legally, which are often more affordable for small
                    operations.
                  </>,
                  <>
                    Estos servicios ofrecen bibliotecas de música previamente
                    autorizadas, lo que elimina la necesidad de múltiples
                    licencias de organización y, al mismo tiempo, ofrece
                    soluciones rentables para reproducir música legalmente en
                    una empresa, que a menudo son más asequibles para
                    operaciones pequeñas.
                  </>,
                  <>
                    Ces services proposent des bibliothèques musicales dont les
                    droits d'utilisation sont déjà réglés, ce qui élimine le
                    besoin de multiples licences auprès de différentes
                    organisations et offre des solutions économiques pour
                    diffuser de la musique légalement dans un commerce,
                    solutions souvent plus abordables pour les petites
                    entreprises.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서비스는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사전
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      허가를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      받은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이브러리를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제공하여
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      여러
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기관으로부터
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      취득해야
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      번거로움을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없애고,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사업장에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합법적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효율적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      솔루션을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제공합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특히
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소규모
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사업장에는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서비스가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더욱
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      경제적일
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisor} />
            <section id="royalty-free-music-vs-licensed">
              <h2 className={classes.subSection}>
                {translate(
                  <>Royalty-Free Music vs. Licensed Music</>,
                  <>Música libre de derechos frente a música con licencia</>,
                  <>Musique libre de droits ou musique sous licence&nbsp;?</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      로열티
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      프리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Royalty-free music implies that you pay a one-time price,
                    and then you are not obliged to continue paying any more
                    royalties. Licensed music, however, contains mainstream
                    songs, and periodic payments have to be made to the
                    licensing organizations.
                  </>,
                  <>
                    La música libre de derechos implica que pagas un precio
                    único y luego no estás obligado a seguir pagando más
                    derechos. La música bajo licencia, sin embargo, contiene
                    canciones populares y se deben realizar pagos periódicos a
                    las organizaciones que otorgan la licencia.
                  </>,
                  <>
                    La musique libre de droits implique de payer un prix unique,
                    après quoi vous n'êtes plus tenu de verser de redevances. La
                    musique sous licence, quant à elle, comprend des chansons
                    populaires, et des paiements périodiques doivent être
                    effectués aux organismes de gestion des droits d'auteur.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      로열티
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      프리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      번만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비용을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지불하면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이상
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      로열티를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지불할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      의무가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없다는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      의미합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반면,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대중적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      곡들을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      포함하며,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      단체에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정기적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용료를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지불해야
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <div className={classes.dataTableWrapper}>
                <table className={classes.dataTable}>
                  <caption className={classes.dtCaption}>
                    {translate(
                      <>Royalty-Free vs. Licensed Music Pros and Cons</>,
                      <>
                        Música libre de derechos de autor frente a música con
                        licencia: ventajas y desventajas.
                      </>,
                      <>
                        Musique libre de droits versus musique sous licence :
                        avantages et inconvénients.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          로열티 프리 음악과 라이선스 음악: 장점과 단점 비교.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col" className={classes.dataTableHeader}>
                        {translate(
                          <>Type</>,
                          <>Tipo</>,
                          <>Taper</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              유형
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </th>
                      <th scope="col" className={classes.dataTableHeader}>
                        {translate(
                          <>Pros</>,
                          <>Positivos</>,
                          <>Points positifs</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              긍정적인
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              점
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </th>
                      <th scope="col" className={classes.dataTableHeader}>
                        {translate(
                          <>Cons</>,
                          <>Negativos</>,
                          <>Négatifs</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              부정적인
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              측면
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Royalty-Free</>,
                          <>Libre de regalías</>,
                          <>Libre de droits</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              로열티
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              프리
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Cost-effective, simple</>,
                          <>Rentable, sencillo</>,
                          <>Économique, simple</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              비용
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              효율적이고
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              간단함
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>No popular songs</>,
                          <>Sin canciones populares</>,
                          <>Aucune chanson populaire</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              인기
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              있는
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              노래는
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              없습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Licensed Music</>,
                          <>Música con licencia</>,
                          <>Musique sous licence</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              라이선스
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              음악
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Popular, recognizable tracks</>,
                          <>Pistas populares y reconocibles</>,
                          <>Des morceaux populaires et reconnaissables</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              인기
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              있고
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              잘
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              알려진
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              곡들
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Ongoing fees, legal risk if unlicensed</>,
                          <>
                            Tarifas continuas, riesgo legal si no se tiene
                            licencia
                          </>,
                          <>
                            Frais récurrents, risque juridique en cas d'absence
                            de licence
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              지속적인
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              수수료
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              발생,
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              무허가
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              시
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              법적
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              위험
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              발생
                            </span>{' '}
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              가능성
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                      <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                      <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    If you want something smarter and stress-free,{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST}
                      title={
                        'Learn more about this premium background music service for businesses, featuring expertly curated playlists.'
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                    is an excellent choice. It provides professionally curated
                    playlists ready to use in business—all commercial licenses
                    are included. No lawyer headaches, no additional steps, just
                    plug and play, and let your shop sound great and be
                    completely compliant.
                  </>,
                  <>
                    Si desea algo más inteligente y sin estrés,{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST_ES}
                      title={
                        'Obtenga más información sobre este servicio de música de fondo de primera calidad para empresas, que incluye listas de reproducción cuidadosamente seleccionadas por expertos.'
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                    es una excelente opción. Proporciona listas de reproducción
                    seleccionadas profesionalmente y listas para usar en los
                    negocios; todas las licencias comerciales están incluidas.
                    Sin dolores de cabeza de abogados, sin pasos adicionales,
                    simplemente conéctelo y listo, y deje que su tienda suene
                    genial y cumpla completamente con las normas.
                  </>,
                  <>
                    Si vous recherchez une solution plus intelligente et sans
                    stress,{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST_FR}
                      title={
                        "Découvrez ce service de musique d'ambiance haut de gamme pour les entreprises, proposant des listes de lecture soigneusement sélectionnées par des experts."
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                    est un excellent choix. Ce service propose des playlists
                    soigneusement sélectionnées par des professionnels, prêtes à
                    l'emploi pour votre entreprise, avec toutes les licences
                    commerciales incluses. Fini les tracas juridiques et les
                    démarches administratives&nbsp;: branchez, lancez la musique
                    et profitez d'une ambiance sonore parfaite et conforme à la
                    réglementation.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더욱
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스마트하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      솔루션을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      원하신다면
                    </span>{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST_KO}
                      title={
                        '기업을 위한 프리미엄 배경 음악 서비스인 이 제품을 통해 전문가가 엄선한 플레이리스트에 대해 자세히 알아보세요.'
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      탁월한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      선택입니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전문가가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      엄선한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      플레이리스트를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제공하며,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상업적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      포함되어
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      복잡한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      법적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      문제나
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      추가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      절차
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      바로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가능하며,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매장을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      멋진
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가득
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      채우고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동시에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저작권
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      규정을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      준수할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisor} />
            <section id="music-licensors-for-small-business">
              <h2 className={classes.subSection}>
                {translate(
                  <>Music Licensing Platforms for Small Businesses</>,
                  <>
                    Plataformas de licencias musicales para pequeñas empresas
                  </>,
                  <>
                    Plateformes de licence musicale pour les petites entreprises
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소규모
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기업을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      플랫폼
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id="music-licensor-fees-and-best-use-cases">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      The following platforms assist you in getting both music
                      and licensing in a single package:
                    </>,
                    <>
                      Las siguientes plataformas le ayudan a obtener música y
                      licencias en un solo paquete:
                    </>,
                    <>
                      Les plateformes suivantes vous permettent d'obtenir à la
                      fois la musique et les licences nécessaires en un seul et
                      même forfait&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        플랫폼들은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이선스를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        번에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        얻을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있도록
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도와줍니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <div className={classes.dataTableWrapper}>
                  <table className={classes.dataTable}>
                    <caption className={classes.dtCaption}>
                      {translate(
                        <>
                          Music Streaming Service/Licensor Platform by Fee and
                          Venue Suitability
                        </>,
                        <>
                          Esta es una plataforma que ofrece servicios de
                          transmisión y licencia de música, clasificados según
                          su idoneidad en función del precio y el uso previsto.
                        </>,
                        <>
                          Plateforme de services de diffusion de musique en
                          continu/de concession de licences, classée selon les
                          tarifs et l'adéquation au lieu d'utilisation.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            음악 스트리밍/라이선스 서비스를 제공하는 플랫폼으로,
                            가격 및 사용 목적에 따른 적합성을 기준으로 분류되어
                            있습니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>Platform</>,
                            <>Plataforma</>,
                            <>Plate-forme</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                플랫폼
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>Best&nbsp;For</>,
                            <>La&nbsp;mejor&nbsp;para:</>,
                            <>Idéal&nbsp;pour&nbsp;:</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                가장
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                적합한
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                대상
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                        <th scope="col" className={classes.dataTableHeader}>
                          {translate(
                            <>Cost&nbsp;Estimate</>,
                            <>Estimación de&nbsp;costos</>,
                            <>Estimation des&nbsp;coûts</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                비용
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                추정
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className={classes.dataTableField}>
                          Cloud Cover Music
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Retail, gyms, cafes</>,
                            <>Comercio minorista, gimnasios, cafeterías</>,
                            <>Commerces de détail, salles de sport, cafés</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                소매점,
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                헬스장,
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                카페
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>From $16.16/month</>,
                            <>Desde $16,16/mes</>,
                            <>À partir de 16,16&nbsp;$ par mois</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                월
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                16.16달러부터
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>
                          Soundtrack Your Brand
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Spotify-like experience</>,
                            <>Experiencia similar a&nbsp;Spotify</>,
                            <>
                              Une expérience similaire à&nbsp;celle
                              de&nbsp;Spotify
                            </>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                스포티파이와
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                유사한
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                경험
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>$34/month</>,
                            <>$34/mes</>,
                            <>34&nbsp;$ par&nbsp;mois</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                월
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                34달러
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>Rockbot</td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Multi-location retailers</>,
                            <>Minoristas&nbsp;con múltiples&nbsp;ubicaciones</>,
                            <>Détaillants multi-sites</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                다중
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                매장
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                소매업체
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Custom pricing</>,
                            <>Precios personalizados</>,
                            <>Tarification personnalisée</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                맞춤
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                가격
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>Mood Media</td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Chain businesses</>,
                            <>Negocios en&nbsp;cadena</>,
                            <>Entreprises de&nbsp;chaînes</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                체인
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                사업
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                        <td className={classes.dataTableField}>
                          {translate(
                            <>Varies by plan</>,
                            <>Varía&nbsp;según el&nbsp;plan</>,
                            <>Varie&nbsp;selon le&nbsp;forfait</>,
                            <>
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                요금제에
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                따라
                              </span>{' '}
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                다릅니다.
                              </span>
                            </>,
                            selectedLanguage
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={classes.dataTableField}>
                          <a
                            className={classes.aHref}
                            target="_blank"
                            href={translate(
                              MAIN_SITE_ENVIRONMENT_HOST,
                              MAIN_SITE_ENVIRONMENT_HOST_ES,
                              MAIN_SITE_ENVIRONMENT_HOST_FR,
                              MAIN_SITE_ENVIRONMENT_HOST_KO,
                              selectedLanguage
                            )}
                            title={translate(
                              'This is a background music service with flexible, budget-friendly plans for businesses.',
                              'Este servicio es un servicio de música ambiental que ofrece planes de precios flexibles y económicos para empresas.',
                              "Ce service propose de la musique d'ambiance et offre des forfaits tarifaires flexibles et abordables pour les entreprises.",
                              '이 서비스는 기업을 위한 유연하고 예산 친화적인 가격 플랜을 제공하는 배경 음악 서비스입니다.',
                              selectedLanguage
                            )}
                            rel="noopener noreferrer"
                          >
                            <span translate="no">
                              Practical&nbsp;
                              <span className={classes.spanPre}>
                                Stream&nbsp;
                                <IconExternalLink
                                  size={
                                    width < 500 ? 17 : width < 920 ? 20 : 24
                                  }
                                  width={
                                    width < 500 ? 17 : width < 920 ? 20 : 24
                                  }
                                  height={
                                    width < 500 ? 17 : width < 920 ? 20 : 24
                                  }
                                />
                              </span>
                            </span>
                          </a>
                        </td>
                        <td className={classes.dataTableField}>
                          <span className={classes.spanGood}>
                            {translate(
                              <>All business types</>,
                              <>Todo tipo de negocios</>,
                              <>Tous&nbsp;types d'entreprises</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  모든
                                </span>{' '}
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  사업
                                </span>{' '}
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  유형
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </span>
                        </td>
                        <td className={classes.dataTableField}>
                          <span className={classes.spanGood}>
                            {translate(
                              <>Flexible, budget-friendly plans</>,
                              <>Planes flexibles y económicos</>,
                              <>
                                Des&nbsp;forfaits flexibles et&nbsp;économiques
                              </>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  유연하고
                                </span>{' '}
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  예산
                                </span>{' '}
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  친화적인
                                </span>{' '}
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  요금제
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                        <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    With these platforms, your shop background music license
                    cost includes both music access and legal coverage.
                  </>,
                  <>
                    Con estas plataformas, el costo de la licencia de música de
                    fondo de tu tienda incluye tanto el acceso a la música como
                    la cobertura legal.
                  </>,
                  <>
                    Grâce à ces plateformes, le coût de la licence de musique
                    d'ambiance pour votre magasin inclut à la fois l'accès à la
                    musique et la couverture juridique.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      플랫폼을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이용하면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      배경
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비용에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      권한과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      법적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보호가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모두
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      포함됩니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisor} />
            <section id="music-compliance-tips">
              <h2 className={classes.subSection}>
                {translate(
                  <>Music Clearance and Compliance Tips</>,
                  <>Consejos sobre autorización y cumplimiento de la música</>,
                  <>
                    Conseils en matière d'autorisation et de conformité
                    musicales
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저작권
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      허가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      및
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      준수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관련
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      팁
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Music clearance is the process of having legal permission to
                    play a track publicly. This is how to keep legal music use
                    in business:
                  </>,
                  <>
                    La autorización musical es el proceso de obtener permiso
                    legal para reproducir una pista públicamente. Así es como se
                    mantiene el uso legal de la música en los negocios:
                  </>,
                  <>
                    L'autorisation d'utilisation musicale est le processus qui
                    consiste à obtenir la permission légale de diffuser un
                    morceau de musique en public. Voici comment garantir une
                    utilisation légale de la musique dans le cadre
                    professionnel&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      허가란
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특정
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공개적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      법적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      허가를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      받는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과정입니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사업장에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합법적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방법은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      같습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Document everything:
                      </span>{' '}
                      Keep records of all licenses and payments.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Documente todo:
                      </span>{' '}
                      mantenga registros de todas las licencias y pagos.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Documentez tout&nbsp;:
                      </span>{' '}
                      Conservez des traces de toutes les licences et de tous les
                      paiements.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        모든
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문서화하세요.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        모든
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        면허증과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지불
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        내역을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기록으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        남기세요.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Understand your coverage:
                      </span>{' '}
                      Know exactly what each license covers.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Comprenda su cobertura:
                      </span>{' '}
                      sepa exactamente qué cubre cada licencia.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Comprenez votre couverture&nbsp;:
                      </span>{' '}
                      Sachez exactement ce que chaque licence couvre.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        내용을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정확히
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이해하세요:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        각
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이선스가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        무엇을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보장하는지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정확히
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        알아두세요.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Regular audits:
                      </span>{' '}
                      Periodically review your music usage and licensing needs.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Auditorías periódicas:
                      </span>{' '}
                      revise periódicamente su uso de música y sus necesidades
                      de licencias.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Audits réguliers&nbsp;:
                      </span>{' '}
                      Examinez périodiquement votre utilisation de la musique et
                      vos besoins en matière de licences.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정기적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감사:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사용
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요구
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사항을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주기적으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        검토하십시오.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Stay informed:</span>{' '}
                      Copyright laws and rates change periodically.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Manténgase informado:
                      </span>{' '}
                      las leyes y tarifas de derechos de autor cambian
                      periódicamente.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Restez informé&nbsp;:
                      </span>{' '}
                      Les lois et les tarifs relatifs aux droits d'auteur
                      évoluent périodiquement.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        최신
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정보를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        확인하세요:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        저작권법과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요금은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주기적으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        변경됩니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Seek professional help:
                      </span>{' '}
                      Consider consulting with entertainment lawyers for complex
                      situations.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Busque ayuda profesional:
                      </span>{' '}
                      considere consultar con abogados de entretenimiento para
                      situaciones complejas.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Demandez l'aide d'un professionnel&nbsp;:
                      </span>{' '}
                      Envisagez de consulter des avocats spécialisés dans le
                      droit du divertissement pour les situations complexes.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전문가의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도움을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        받으세요:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        복잡한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상황의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        경우
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        엔터테인먼트
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전문
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        변호사와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상담하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고려해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보세요.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
            </section>
            <hr className={classes.threeDotDivisor} />
            <section id="music-licensing-faq-for-small-shops">
              <h2 className={classes.subSection}>
                {translate(
                  <>FAQs About Music Licensing for Small Shops</>,
                  <>
                    Preguntas frecuentes sobre licencias de música para pequeñas
                    tiendas
                  </>,
                  <>
                    Questions fréquentes sur les licences musicales pour les
                    petits commerces
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소규모
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매장을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관련
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      자주
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      묻는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      질문(FAQ)
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <ol className={classes.standardText + ' ' + ' ol-list-white'}>
                <li
                  className={classes.liOlPoint}
                  style={{
                    marginBottom: '15px',
                  }}
                >
                  {translate(
                    <>
                      <section id="can-a-small-shop-use-spotify">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          Can I use Spotify in my store?
                        </h3>{' '}
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          No, the standard Spotify subscriptions do not provide
                          a public performance licence to shops.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="can-a-small-shop-use-spotify">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          ¿Puedo usar Spotify en mi tienda?
                        </h3>{' '}
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          No, las suscripciones estándar de Spotify no
                          proporcionan una licencia de interpretación pública a
                          las tiendas.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="can-a-small-shop-use-spotify">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          Puis-je utiliser Spotify dans mon magasin&nbsp;?
                        </h3>{' '}
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          Non, les abonnements Spotify standard n'incluent pas
                          de licence de diffusion publique pour les commerces.
                        </p>
                      </section>
                    </>,
                    <>
                      <h3
                        className={
                          classes.spanBolder + ' ' + classes.pParagraph
                        }
                        style={{ marginBottom: '10px' }}
                      >
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          제
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          가게에서
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          스포티파이를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          사용할
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          있나요?
                        </span>
                      </h3>
                      <p
                        className={classes.spanItalic}
                        style={{ marginTop: '5px' }}
                      >
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          아니요,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          일반
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스포티파이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구독으로는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          매장에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공개적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          재생할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          라이선스가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제공되지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          않습니다.
                        </span>
                      </p>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li
                  className={classes.liOlPoint}
                  style={{
                    marginBottom: '15px',
                  }}
                >
                  {translate(
                    <>
                      <section id="playing-unlicensed-music-consequences">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          What happens when I play music without a license?
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          Licensing authorities can issue fines, backdated fees,
                          and legal notices.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="playing-unlicensed-music-consequences">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          ¿Qué pasa cuando reproduzco música sin licencia?
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          Las autoridades otorgantes de licencias pueden imponer
                          multas, tarifas retroactivas y avisos legales.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="playing-unlicensed-music-consequences">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          Que se passe-t-il si je diffuse de la musique sans
                          licence&nbsp;?
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          Les organismes de gestion des droits d'auteur peuvent
                          infliger des amendes, réclamer des redevances
                          rétroactives et envoyer des mises en demeure.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="playing-unlicensed-music-consequences">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            라이선스
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            없이
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            음악을
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            재생하면
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            어떻게
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            되나요?
                          </span>
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            저작권
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            관리
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            기관은
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            벌금
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            부과,
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            소급
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            사용료
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            청구,
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            법적
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            조치
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            등을
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            취할
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            수
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            있습니다.
                          </span>
                        </p>
                      </section>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li
                  className={classes.liOlPoint}
                  style={{
                    marginBottom: '15px',
                  }}
                >
                  {translate(
                    <>
                      <section id="one-license-per-store">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          Is one license enough if I have multiple stores?
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          Each location typically requires separate licensing,
                          though some organizations offer multi-location
                          discounts for commercial music license cost
                          calculations.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="one-license-per-store">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          ¿Es suficiente una licencia si tengo varias tiendas?
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          Cada ubicación generalmente requiere una licencia
                          separada, aunque algunas organizaciones ofrecen
                          descuentos para múltiples ubicaciones para los
                          cálculos de costos de licencias de música comercial.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="one-license-per-store">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          Une seule licence est-elle suffisante si j'ai
                          plusieurs magasins&nbsp;?
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          Chaque établissement nécessite généralement une
                          licence distincte, bien que certaines organisations
                          proposent des réductions pour les établissements
                          multiples lors du calcul du coût des licences de
                          musique commerciale.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="one-license-per-store">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            매장이
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            여러
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            개
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            있는
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            경우
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            라이선스
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            하나로
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            충분할까요?
                          </span>
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            일반적으로
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            각
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            매장마다
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            별도의
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            라이선스가
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            필요하지만,
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            일부
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            단체에서는
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            상업용
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            음악
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            라이선스
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            비용
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            산정
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            시
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            여러
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            매장에
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            대한
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            할인
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            혜택을
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            제공하기도
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            합니다.
                          </span>
                        </p>
                      </section>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li
                  className={classes.liOlPoint}
                  style={{
                    marginBottom: '15px',
                  }}
                >
                  {translate(
                    <>
                      <section id="one-license-per-store">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          Do I need different licenses for different types of
                          music?
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          It depends on your music sources. Comprehensive PRO
                          licenses cover most scenarios, but specific situations
                          may require additional clearances.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="one-license-per-store">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          ¿Necesito diferentes licencias para diferentes tipos
                          de música?
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          Depende de tus fuentes de música. Las licencias PRO
                          integrales cubren la mayoría de los escenarios, pero
                          situaciones específicas pueden requerir autorizaciones
                          adicionales.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="one-license-per-store">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          Ai-je besoin de licences différentes pour différents
                          types de musique&nbsp;?
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          Cela dépend de vos sources musicales. Les licences
                          complètes des sociétés de gestion des droits d'auteur
                          couvrent la plupart des cas, mais certaines situations
                          peuvent nécessiter des autorisations supplémentaires.
                        </p>
                      </section>
                    </>,
                    <>
                      <section id="one-license-per-store">
                        <h3
                          className={
                            classes.spanBolder + ' ' + classes.pParagraph
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            음악
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            종류에
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            따라
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            다른
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            라이선스가
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            필요한가요?
                          </span>
                        </h3>
                        <p
                          className={classes.spanItalic}
                          style={{ marginTop: '5px' }}
                        >
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            사용하시는
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            음악
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            소스에
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            따라
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            다릅니다.
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            포괄적인
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            저작권
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            관리
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            단체(PRO)
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            라이선스는
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            대부분의
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            경우를
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            커버하지만,
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            특정
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            상황에서는
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            추가적인
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            허가가
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            필요할
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            수
                          </span>{' '}
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            있습니다.
                          </span>
                        </p>
                      </section>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ol>
            </section>
            <section id="concluding-remarks">
              <h2 className={classes.subSection}>
                {translate(
                  <>Final Thoughts</>,
                  <>Pensamientos finales</>,
                  <>Réflexions finales</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      마무리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생각
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Obtaining the appropriate music license does not need to be
                    difficult. With{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST}
                      title={
                        "This is a business background music service that's easy to set up and provides complete legal coverage."
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                    playing licensed music in your shop is simple, legal, and
                    hassle-free&nbsp;— just press play and set the mood.
                  </>,
                  <>
                    Obtener la licencia musical adecuada no tiene por qué ser
                    difícil. Con{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST_ES}
                      title={
                        'Este servicio es un servicio de música ambiental que ofrece planes de precios flexibles y económicos para empresas.'
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                    reproducir música con licencia en tu tienda es simple, legal
                    y sin complicaciones: solo presiona reproducir y crea el
                    ambiente.
                  </>,
                  <>
                    Obtenir la licence musicale appropriée n'a rien de
                    compliqué. Avec{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST_FR}
                      title={
                        "Ce service propose de la musique d'ambiance et offre des forfaits tarifaires flexibles et abordables pour les entreprises."
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                    diffuser de la musique sous licence dans votre magasin est
                    simple, légal et sans tracas&nbsp;: il vous suffit d'appuyer
                    sur lecture et de créer l'ambiance souhaitée.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적절한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      확보하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어렵지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않습니다.
                    </span>{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST_KO}
                      title={
                        '이 서비스는 설치가 간편하고 모든 법적 문제를 해결해 주는 기업용 배경 음악 서비스입니다'
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이용하면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매장에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      간단하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합법적으로,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그리고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      번거로움
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      버튼만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      누르면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      원하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분위기를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연출할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisorBottomOfArticle} />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
            <br />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <ReadyToTransform
              selectedLanguage={selectedLanguage}
              linkVariant={false}
            />
          </Grid.Col>
          <Grid.Col md={24}>
            <div>
              <br />
              <br />
              <br />
            </div>
          </Grid.Col>
        </Grid>
      </div>
    </div>
  )
}
